/**
 * 奇門遁甲排盤引擎（轉盤 / 拆補）
 * 邏輯對照 Excel Program：EE/EN 天盤合成、FF 八門、拆補局數；節氣預表對齊 This_Yr/Last_Yr；農曆用預表。
 */
import jiazi60 from '../data/jiazi60.json';
import yearPillars from '../data/year_pillars.json';
import jieqiDun from '../data/jieqi_dun.json';
import jieqiTable from '../data/jieqi_1900_2100.json';
import lunarTable from '../data/lunar_1900_2100.json';
import rotation from '../data/rotation.json';
import hoursData from '../data/hours.json';
import plateRotation from '../data/plate_rotation.json';
import doorRotation from '../data/door_rotation.json';
import markersData from '../data/markers.json';

export type DunType = '阳遁' | '阴遁';

export interface QimenInput {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  clockwise?: number;
  antiClockwise?: number;
  /** 手動覆寫陰陽遁 */
  dunOverride?: DunType | '';
  /** 手動覆寫局數 1–9 */
  juOverride?: number | null;
}

export interface PalaceContent {
  luoshu: number;
  name: string;
  diGan: string; // 地盤天干
  tianGan: string; // 天盤天干
  /** 天盤九星（旋轉後；別名 tianPanXing） */
  star: string;
  /** 地盤九星／地盤天星（Program BA：洛書固定星，不旋轉） */
  diPanXing: string;
  /** 天盤九星（與 star 相同，供 UI／對照） */
  tianPanXing: string;
  door: string; // 八門
  shen: string; // 天盤八神
  /** 地盤八神（Program AL/BD；與天盤八神並陳於宮頂） */
  diShen: string;
  /** 引干（Program AS：AR17 则 AR=CA/CU，否则 AQ=BW/CQ＝天盘干按值使→时干阳圈偏移旋转） */
  yinGan: string;
  /** 中五寄坤寄顯（天盤干；Program G10=IF(F10=AK6,AK9,"")） */
  jiXianTian: string;
  /** 中五寄坤寄顯（地盤干；Program G11=IF(F11=AK6,AK9,"")） */
  jiXianDi: string;
  /** 空亡（時旬空亡宮） */
  kongWang: boolean;
  /** 馬星／驛馬 */
  maXing: boolean;
  /** 迫（門迫） */
  po: boolean;
  /** 刑（Program F12/L12/R12…） */
  xing: boolean;
  /** 墓（Program G12/S12/G24/S24…） */
  mu: boolean;
}

export interface QimenResult {
  input: QimenInput;
  effective: {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    nextDayZi: boolean;
  };
  pillars: {
    year: string;
    month: string;
    day: string;
    hour: string;
  };
  jieqi: { name: string; dun: DunType; zhi: string };
  dun: DunType;
  ju: number;
  juLabel: string;
  xunshou: string;
  xunshouStem: string;
  zhifu: { star: string; palace: number; palaceName: string };
  zhishi: { door: string; palace: number; palaceName: string };
  rotation: number;
  palaces: PalaceContent[];
  /** 九宮顯示順序（含順逆轉後）：[NW,N,NE, W,C,E, SW,S,SE] 對應洛書號 */
  displayOrder: number[];
  /** 空亡宮洛書號（時柱旬首） */
  kongWangPalaces: number[];
  /** 馬星宮洛書號（時支驛馬） */
  maXingPalace: number | null;
  /** 八門伏吟／反吟；天干伏吟／反吟（對照 Program T8/T9） */
  menFuyin: '' | '八門伏吟' | '八門反吟';
  ganFuyin: '' | '天干伏吟' | '天干反吟';
  /** 旬首入中宮／顯示引干切換旗標（Program AR17） */
  xunshouInZhong: boolean;
  notes: string[];
  fidelity: {
    pillars: 'real' | 'approx' | 'stub';
    jieqi: 'real' | 'approx' | 'stub';
    ju: 'real' | 'approx' | 'stub';
    plates: 'real' | 'approx' | 'stub';
    markers: 'real' | 'approx' | 'stub';
  };
}

const TIANGAN = '甲乙丙丁戊己庚辛壬癸';
const DIZHI = '子丑寅卯辰巳午未申酉戌亥';

/** 洛書號 → 宮名（對照 Program AJ「後」欄：1坎…） */
const PALACE_NAME: Record<number, string> = {
  1: '坎',
  2: '坤',
  3: '震',
  4: '巽',
  5: '中',
  6: '乾',
  7: '兌',
  8: '艮',
  9: '離',
};

/** 洛書宮位對應地盤九星／八門（對照 Program BA「地盤天星」／BB；BA9 存「禽」） */
const PALACE_STAR: Record<number, string> = {
  1: '蓬',
  2: '芮',
  3: '冲',
  4: '辅',
  5: '禽',
  6: '心',
  7: '柱',
  8: '任',
  9: '英',
};

/**
 * Excel UI never shows 禽：值符 AC18 禽→芮；Worksheet 盤面亦不出現「禽」。
 * Keep BA internal id 禽；all UI-facing star fields output 芮.
 */
function starLabel(s: string): string {
  return s === '禽' ? '芮' : s;
}
const PALACE_DOOR: Record<number, string> = {
  1: '休',
  2: '死',
  3: '伤',
  4: '杜',
  5: '-',
  6: '开',
  7: '惊',
  8: '生',
  9: '景',
};

const LIUYI = ['戊', '己', '庚', '辛', '壬', '癸', '丁', '丙', '乙'];
const JU_LABELS = ['', '一局', '二局', '三局', '四局', '五局', '六局', '七局', '八局', '九局'];

/** 陰遁八門逆佈圓週（對照 Program 逆行） */
const DOOR_CIRCLE_YIN = [1, 6, 7, 2, 9, 4, 3, 8];

type PlateRot = {
  ee: { headers: number[]; rows: (number | string)[][] };
  en: { headers: number[]; rows: (number | string)[][] };
  bashen_index: Record<string, string>;
};
const PLATE = plateRotation as PlateRot;
const DOOR_ROT = doorRotation as { headers: number[]; rows: number[][] };
type XingMuRule = {
  anyOf: string[];
  equalPair?: string[];
  pairMode?: 'tian_extra_vs_di' | 'cross';
  slots: string[];
  matchXunshouStem?: boolean;
} | null;

const MARKERS = markersData as {
  kongWangByXunshou: Record<string, number[]>;
  maXingByHourZhiGroup: Record<string, number>;
  poByPalace: Record<string, string[]>;
  xingMu: {
    /** @deprecated kept for docs; 寄顯 uses jiXianExtra(gan, kunDi, zhongDi) */
    guiToYi?: { from: string; to: string };
    byPalace: Record<string, { xing: XingMuRule; mu: XingMuRule }>;
  };
};

type JiaziRow = {
  gz: string;
  gan: string;
  zhi: string;
  xunshou: string;
  idx: number;
};

const JIAZI = jiazi60 as JiaziRow[];
const YEAR_PILLARS = yearPillars as Record<string, string>;
const JIEQI_DUN = jieqiDun as { name: string; zhi: string; dun: DunType }[];
const JIEQI_BY_YEAR = jieqiTable as Record<string, { n: string; t: string }[]>;
const LUNAR = lunarTable as Record<string, number>;
const HOURS = hoursData as { start: number; end: number; zhi: string; nextDay: boolean }[];

function mod(n: number, m: number): number {
  return ((n % m) + m) % m;
}

/** Excel 1900 日期序號（對照 Program 日柱 MID/MOD 公式） */
export function excelSerial(y: number, m: number, d: number, h = 0, mi = 0): number {
  const epoch = Date.UTC(1899, 11, 30, 0, 0, 0);
  const t = Date.UTC(y, m - 1, d, h, mi, 0);
  return (t - epoch) / 86400000;
}

function ganIndex(g: string): number {
  return TIANGAN.indexOf(g) + 1; // 1–10
}

function zhiIndex(z: string): number {
  return DIZHI.indexOf(z) + 1; // 1–12
}

/** 月建序：寅=1 … 丑=12（Program DI） */
function monthOrdFromZhi(z: string): number {
  const map: Record<string, number> = {
    寅: 1,
    卯: 2,
    辰: 3,
    巳: 4,
    午: 5,
    未: 6,
    申: 7,
    酉: 8,
    戌: 9,
    亥: 10,
    子: 11,
    丑: 12,
  };
  return map[z] ?? 1;
}

function dayPillarFromSerial(serial: number): { gan: string; zhi: string; gz: string } {
  const s = Math.floor(serial);
  const gan = TIANGAN[mod(s - 2, 10)];
  const zhi = DIZHI[mod(s - 4, 12)];
  return { gan, zhi, gz: gan + zhi };
}

function monthGan(yearGan: string, monthOrd: number): string {
  const ac4 = ganIndex(yearGan);
  let m = mod(ac4 * 2 + monthOrd, 10);
  if (m === 0) m = 10;
  return TIANGAN[m - 1];
}

function hourGan(dayGan: string, hourZhi: string): string {
  const dg = ganIndex(dayGan);
  const hz = zhiIndex(hourZhi);
  let m = mod((dg - 1) * 2 + hz, 10);
  if (m === 0) m = 10;
  return TIANGAN[m - 1];
}

function hourZhiInfo(hour: number, minute: number): { zhi: string; nextDay: boolean } {
  const t = hour + minute / 60;
  for (const h of HOURS) {
    if (h.start === 0 && h.end === 1) {
      if (t >= 0 && t < 1) return { zhi: h.zhi, nextDay: false };
    } else if (t >= h.start && t < h.end) {
      return { zhi: h.zhi, nextDay: h.nextDay };
    }
  }
  return { zhi: '子', nextDay: false };
}

function normalizeJieqiName(n: string): string {
  return n
    .replace(/穀/g, '谷')
    .replace(/驚/g, '惊')
    .replace(/蟄/g, '蛰')
    .replace(/處/g, '处')
    .replace(/滿/g, '满')
    .replace(/雪$/g, '雪');
}

function findJieqi(y: number, m: number, d: number, h: number, mi: number): {
  name: string;
  dun: DunType;
  zhi: string;
} {
  const prev = JIEQI_BY_YEAR[String(y - 1)] ?? [];
  const curr = JIEQI_BY_YEAR[String(y)] ?? [];
  const timeline: { name: string; t: number; dun: DunType; zhi: string }[] = [];

  const dunOf = (nameHans: string): { dun: DunType; zhi: string; name: string } => {
    const simplified = normalizeJieqiName(nameHans);
    const hit = JIEQI_DUN.find(
      (j) => normalizeJieqiName(j.name) === simplified || j.name === nameHans,
    );
    if (hit) return { dun: hit.dun, zhi: hit.zhi, name: hit.name };
    const yang = ['冬至', '小寒', '大寒', '立春', '雨水', '惊蛰', '春分', '清明', '谷雨', '立夏', '小满', '芒种'];
    const isYang = yang.some((x) => simplified.startsWith(x.slice(0, 2)));
    return { dun: isYang ? '阳遁' : '阴遁', zhi: '子', name: nameHans };
  };

  for (const jq of prev) {
    if (['大雪', '冬至'].includes(jq.n)) {
      const meta = dunOf(jq.n);
      timeline.push({ name: meta.name, t: Date.parse(jq.t), dun: meta.dun, zhi: meta.zhi });
    }
  }
  for (const jq of curr) {
    const meta = dunOf(jq.n);
    timeline.push({ name: meta.name, t: Date.parse(jq.t), dun: meta.dun, zhi: meta.zhi });
  }

  timeline.sort((a, b) => a.t - b.t);
  const localMs = new Date(y, m - 1, d, h, mi).getTime();

  let current = timeline[0] ?? { name: '冬至', t: 0, dun: '阳遁' as DunType, zhi: '子' };
  for (const item of timeline) {
    if (item.t <= localMs) current = item;
    else break;
  }
  return { name: current.name, dun: current.dun, zhi: current.zhi };
}

function getLichun(y: number): Date | null {
  const list = JIEQI_BY_YEAR[String(y)];
  if (!list) return null;
  const lc = list.find((j) => j.n === '立春');
  return lc ? new Date(lc.t) : null;
}

function yearPillar(y: number, dt: Date): string {
  const lichun = getLichun(y);
  let yy = y;
  if (lichun && dt < lichun) yy = y - 1;
  return (
    YEAR_PILLARS[String(yy)] ??
    (() => {
      const idx = mod(yy - 1864, 60);
      return JIAZI[idx].gz;
    })()
  );
}

function xunInfo(gz: string): { xunshouGz: string; xunshouStem: string; idx: number; offset: number } {
  const row = JIAZI.find((j) => j.gz === gz);
  if (!row) return { xunshouGz: '甲子', xunshouStem: '戊', idx: 1, offset: 0 };
  const startIdx = row.idx - ganIndex(row.gan) + 1;
  const start = JIAZI.find((j) => j.idx === startIdx) ?? JIAZI[0];
  return {
    xunshouGz: start.gz,
    xunshouStem: start.xunshou,
    idx: row.idx,
    offset: row.idx - startIdx,
  };
}

/** 地盤六儀三奇 */
function buildDipan(ju: number, dun: DunType): Record<number, string> {
  const dipan: Record<number, string> = {};
  for (let i = 0; i < 9; i++) {
    const palace = dun === '阳遁' ? mod(ju - 1 + i, 9) + 1 : mod(ju - 1 - i, 9) + 1;
    dipan[palace] = LIUYI[i];
  }
  return dipan;
}

function palaceOfStem(dipan: Record<number, string>, stem: string): number {
  for (let p = 1; p <= 9; p++) {
    if (dipan[p] === stem) return p;
  }
  return 5;
}

/** 寄宮：中五 → 坤二 */
function escapeZhong(p: number): number {
  return p === 5 ? 2 : p;
}

function hlookupMatrix(
  headers: number[],
  rows: (number | string)[][],
  key: number,
  palace: number,
): number | null {
  const col = headers.indexOf(key);
  if (col < 0) return null;
  const val = rows[palace - 1]?.[col];
  if (val === '-' || val === undefined || val === null) return null;
  return typeof val === 'number' ? val : Number(val);
}

/**
 * 天盤天干／九星／天盤八神 — 重放 Program EE/EN 與 BF–BK / CB–CE 合成。
 */
function buildTianpanExcel(
  dipan: Record<number, string>,
  zhifuPalace: number,
  hourStemPalace: number,
  dun: DunType,
): {
  tianGan: Record<number, string>;
  star: Record<number, string>;
  diShen: Record<number, string>;
  tianShen: Record<number, string>;
} {
  const Z = zhifuPalace;
  const T = hourStemPalace;
  const bc: Record<number, number | null> = {};
  const bi: Record<number, number | null> = {};

  if (dun === '阳遁') {
    for (let p = 1; p <= 9; p++) {
      bc[p] = hlookupMatrix(PLATE.ee.headers, PLATE.ee.rows, Z, p);
      bi[p] = hlookupMatrix(PLATE.ee.headers, PLATE.ee.rows, T, p);
    }
  } else {
    for (let p = 1; p <= 9; p++) {
      bc[p] = hlookupMatrix(PLATE.en.headers, PLATE.en.rows, -Z, p);
      bi[p] = hlookupMatrix(PLATE.en.headers, PLATE.en.rows, -T, p);
    }
  }

  const idxMap = PLATE.bashen_index;
  const diShen: Record<number, string> = {};
  for (let p = 1; p <= 9; p++) {
    const k = bc[p];
    if (k == null) diShen[p] = '-';
    else diShen[p] = idxMap[String(k)] ?? '-';
  }

  const tianGan: Record<number, string> = {};
  const star: Record<number, string> = {};
  const tianShen: Record<number, string> = {};

  for (let p = 1; p <= 9; p++) {
    const biv = bi[p];
    if (biv == null) {
      tianGan[p] = dipan[p];
      star[p] = PALACE_STAR[p];
      tianShen[p] = diShen[p];
      continue;
    }
    let q: number | null = null;
    for (let cand = 1; cand <= 9; cand++) {
      if (bc[cand] === biv) {
        q = cand;
        break;
      }
    }
    if (q == null) {
      tianGan[p] = dipan[p];
      star[p] = PALACE_STAR[p];
      tianShen[p] = diShen[p];
    } else {
      tianGan[p] = dipan[q];
      star[p] = PALACE_STAR[q];
      tianShen[p] = diShen[q];
    }
  }
  return { tianGan, star, diShen, tianShen };
}

/** 八門 — 重放 Program BN14/CH14 + FF:FM */
function buildDoorsExcel(
  zhifuPalace: number,
  hourOffsetInXun: number,
  dun: DunType,
): { doors: Record<number, string>; zhishiPalace: number } {
  let dest: number;
  if (dun === '阳遁') {
    dest = zhifuPalace + hourOffsetInXun;
    if (dest > 9) dest -= 9;
  } else {
    dest = zhifuPalace - hourOffsetInXun;
    if (dest <= 0) dest += 9;
  }
  if (dest === 5) dest = 2;

  const zhishiDoorRaw = PALACE_DOOR[zhifuPalace] === '-' ? '死' : PALACE_DOOR[zhifuPalace];
  let home = 2;
  for (let p = 1; p <= 9; p++) {
    if (PALACE_DOOR[p] === zhishiDoorRaw) {
      home = p;
      break;
    }
  }
  if (zhishiDoorRaw === '死') home = 2;

  const doors: Record<number, string> = { 5: '' };

  if (dun === '阳遁') {
    const rows = DOOR_ROT.rows;
    let off = 0;
    const homeRow = rows[home - 1];
    const found = homeRow.indexOf(dest);
    if (found >= 0) off = found;
    for (let hp = 1; hp <= 9; hp++) {
      if (hp === 5) continue;
      const dpalace = rows[hp - 1][off];
      const d = PALACE_DOOR[hp];
      doors[dpalace] = d === '-' ? '' : d;
    }
  } else {
    const circle = DOOR_CIRCLE_YIN;
    const homeP = escapeZhong(home);
    const ih = circle.indexOf(homeP);
    const it = circle.indexOf(dest);
    const steps = mod(it - ih, 8);
    for (let i = 0; i < 8; i++) {
      const p = circle[i];
      const src = circle[mod(i - steps, 8)];
      const d = PALACE_DOOR[src];
      doors[p] = d === '-' ? '' : d;
    }
  }

  return { doors, zhishiPalace: dest };
}


/** 時旬空亡宮（對照 Program D9/J9/P9…：甲子→乾…） */
function kongWangPalaces(xunshouGz: string): number[] {
  return MARKERS.kongWangByXunshou[xunshouGz] ?? [];
}

/** 驛馬／馬星宮（對照 Program D12/P12/D24/P24） */
function maXingPalace(hourZhi: string): number | null {
  for (const [group, palace] of Object.entries(MARKERS.maXingByHourZhiGroup)) {
    if (group.includes(hourZhi)) return palace;
  }
  return null;
}

/** 門迫：落宮門是否在 Program 迫表內 */
function isPo(palace: number, door: string): boolean {
  if (!door) return false;
  const list = MARKERS.poByPalace[String(palace)];
  return !!list && list.includes(door);
}

/**
 * 中五寄坤寄顯（Program G10/M10/S10… = IF(F=AK6, AK9, "")）：
 * AK6＝坤二地盤干、AK9＝中五地盤干。天／地干等於坤地盤干時，旁顯中宮地盤干。
 * （舊註「癸→乙」僅為坤=癸、中=乙 時的特例，非通式。）
 */
function jiXianExtra(gan: string, kunDi: string, zhongDi: string): string {
  return gan && kunDi && gan === kunDi ? zhongDi : '';
}

function slotValues(
  slots: string[],
  tian: string,
  di: string,
  tianExtra: string,
  diExtra: string,
): string[] {
  const map: Record<string, string> = {
    tian,
    di,
    tianExtra,
    diExtra,
  };
  return slots.map((s) => map[s] ?? '').filter(Boolean);
}

/**
 * 刑／墓（對照 Program F12/G12 等）：
 * 檢查天盤／地盤干及中五寄坤寄顯（G10/G11）；坤墓另比時柱旬首六儀（W12）。
 */
function evalXingMu(
  palace: number,
  tian: string,
  di: string,
  xunshouStem: string,
  kunDi: string,
  zhongDi: string,
): { xing: boolean; mu: boolean } {
  const rules = MARKERS.xingMu.byPalace[String(palace)];
  if (!rules) return { xing: false, mu: false };
  const tianExtra = jiXianExtra(tian, kunDi, zhongDi);
  const diExtra = jiXianExtra(di, kunDi, zhongDi);

  const check = (rule: XingMuRule): boolean => {
    if (!rule) return false;
    const vals = slotValues(rule.slots, tian, di, tianExtra, diExtra);
    if (rule.anyOf?.some((g) => vals.includes(g))) return true;
    if (rule.matchXunshouStem && xunshouStem && vals.includes(xunshouStem)) return true;
    const pair = rule.equalPair ?? [];
    if (!pair.length) return false;
    if (rule.pairMode === 'cross') {
      const left = [tian, tianExtra].filter(Boolean);
      const right = [di, diExtra].filter(Boolean);
      for (const a of left) {
        for (const b of right) {
          if (a === b && pair.includes(a)) return true;
        }
      }
      return false;
    }
    // tian_extra_vs_di：AND(tian=di|tianExtra=di) 且值∈equalPair
    if (tian && tian === di && pair.includes(tian)) return true;
    if (tianExtra && tianExtra === di && pair.includes(tianExtra)) return true;
    return false;
  };

  return { xing: check(rules.xing), mu: check(rules.mu) };
}

/**
 * 洛書顺行一步（Program FF18:FM26 陽圈）：1→8→3→4→9→2→7→6→1；中5不動。
 */
const YANG_NEXT: Record<number, number> = {
  1: 8, 8: 3, 3: 4, 4: 9, 9: 2, 2: 7, 7: 6, 6: 1, 5: 5,
};

function yangRotate(p: number, n: number): number {
  if (p === 5) return 5;
  let x = p;
  const steps = ((n % 8) + 8) % 8;
  for (let i = 0; i < steps; i++) x = YANG_NEXT[x] ?? x;
  return x;
}

/** 陽圈步數 Z→T（T 在以 Z 起點的 FF 列中的欄位＝CN14/BT14） */
function yangSteps(from: number, to: number): number {
  if (from === 5 || to === 5) return 0;
  let s = 0;
  let p = from;
  while (p !== to) {
    p = YANG_NEXT[p] ?? p;
    s++;
    if (s > 8) return 0;
  }
  return s;
}

/**
 * 引干常態路徑（Program AQ → 阳遁 BW／阴遁 CQ；对拍 Excel GUI）：
 * yinGan[p] = tianGan[rotate_yang(p, N)]，
 * N = 時干天盤宮 T 在「值使宮起點」FF 陽圈列中的欄位（HLOOKUP(CE14/BK14, FF[值使行], …)）。
 * 時干落中（T=5／BK14=CE14=5）：旁路 BY36/CS36，改以「天盤干＝坤地盤干」之宮為 T'，N=steps(值使→T')。
 * （舊誤：把门飛偏移當 N，致多數時辰與 GUI 全盤錯位；2012-09-11 等少數 N 碰巧相同。）
 */
function buildYinGan(
  tianGan: Record<number, string>,
  diGan: Record<number, string>,
  zhishiPalace: number,
  hourStemOnTian: string,
): Record<number, string> {
  let T = 5;
  for (let p = 1; p <= 9; p++) {
    if (tianGan[p] === hourStemOnTian) {
      T = p;
      break;
    }
  }
  const Z = zhishiPalace === 5 ? 2 : zhishiPalace;
  let ref = T;
  if (T === 5) {
    const kunDi = diGan[2] ?? '';
    ref = 5;
    for (let p = 1; p <= 9; p++) {
      if (tianGan[p] === kunDi) {
        ref = p;
        break;
      }
    }
  }
  const N = yangSteps(Z, ref);
  const yin: Record<number, string> = {};
  for (let p = 1; p <= 9; p++) {
    yin[p] = tianGan[yangRotate(p, N)] ?? '';
  }
  return yin;
}

/** 六儀序號 1–9（戊…乙）；对照 Program CW4:CX12／负键 CW13:CX21 */
function liuyiIndex(stem: string): number {
  const i = LIUYI.indexOf(stem);
  return i >= 0 ? i + 1 : 1;
}

function liuyiStem(n: number): string {
  const k = ((Math.abs(n) - 1) % 9 + 9) % 9;
  return LIUYI[k] ?? '';
}

/**
 * 阳遁「旬首入中宮」引干（Program CA）：
 * BX9 = 旬首六儀序；BZ = MOD(BX9+BY−1,9)（0→9）；
 * CA = VLOOKUP(BZ, AY:AZ) ≡ 六儀干(BZ)。
 * BY 列（宮1…9）= 6,7,8,9,1,2,3,4,5。
 */
function buildYinGanXunshouInZhongYang(xunshouStem: string): Record<number, string> {
  const bx9 = liuyiIndex(xunshouStem);
  const by = [6, 7, 8, 9, 1, 2, 3, 4, 5];
  const yin: Record<number, string> = {};
  for (let i = 0; i < 9; i++) {
    let bz = mod(bx9 + by[i] - 1, 9);
    if (bz === 0) bz = 9;
    yin[i + 1] = liuyiStem(bz);
  }
  return yin;
}

/**
 * 阴遁「旬首入中宮」引干（Program CU 正确意图；对拍 Excel GUI 2026-10-11 23:31）：
 * 工作簿 CU 写 VLOOKUP(CT, AY:AZ) 且 CT=MOD(CR9+CS+1,-9) 对负键得 #N/A（坏公式）。
 * 与阳遁 CA 同构：用 |CS|＝[5,4,3,2,1,9,8,7,6] 做正键偏移，
 * BZ = MOD(CR9+|CS|-1, 9)（0→9）；干 = 六儀(BZ) ≡ VLOOKUP(BZ, AY:AZ／CW4:CX12)。
 * Excel 宫2 双干：A11=IF(AND(AR17,引干=AR坤), AR中, …) → 辛+戊（非寄坤丁）。
 */
function buildYinGanXunshouInZhongYin(xunshouStem: string): Record<number, string> {
  const cr9 = liuyiIndex(xunshouStem);
  const csAbs = [5, 4, 3, 2, 1, 9, 8, 7, 6];
  const yin: Record<number, string> = {};
  for (let i = 0; i < 9; i++) {
    let bz = mod(cr9 + csAbs[i] - 1, 9);
    if (bz === 0) bz = 9;
    yin[i + 1] = liuyiStem(bz);
  }
  return yin;
}

/** 順逆轉：對照 Program AG3=MOD(J6-N6,8) → HLOOKUP FX:GL */
function displayPalaceOrder(rot: number): number[] {
  const headers = rotation.headers as number[];
  const rows = rotation.rows as number[][];
  let col = headers.indexOf(rot as never);
  if (col < 0) {
    const r = mod(rot, 8);
    col = headers.indexOf(r as never);
    if (col < 0) col = 0;
  }
  const pick = (rowIdx: number) => rows[rowIdx][col];
  return [
    pick(3), // NW AG8
    pick(8), // N  AG13
    pick(1), // NE AG6
    pick(2), // W  AG7
    pick(4), // C  AG9
    pick(6), // E  AG11
    pick(7), // SW AG12
    pick(0), // S  AG5
    pick(5), // SE AG10
  ];
}

export function computeQimen(input: QimenInput): QimenResult {
  const notes: string[] = [];
  const clockwise = input.clockwise ?? 0;
  const antiClockwise = input.antiClockwise ?? 0;
  const rot = mod(clockwise - antiClockwise, 8);

  let { year, month, day, hour, minute } = input;
  const hz = hourZhiInfo(hour, minute);
  const nextDayZi = hz.nextDay;

  // Program W2 / DT16：23:00–23:59 用次日 0:00 算日柱，時支仍為子
  let ey = year,
    em = month,
    ed = day;
  if (nextDayZi) {
    const nd = new Date(year, month - 1, day + 1);
    ey = nd.getFullYear();
    em = nd.getMonth() + 1;
    ed = nd.getDate();
    notes.push('23:00–23:59 子時：日柱用次日（對照 Program DT16/W2）');
  }

  const serial = excelSerial(ey, em, ed, nextDayZi ? 0 : hour, nextDayZi ? 0 : minute);
  const dayP = dayPillarFromSerial(serial);

  // Program W4：年柱立春界用 W2（23 時為次日 0:00），非牆鐘原時刻
  const dtForYear = nextDayZi
    ? new Date(ey, em - 1, ed, 0, 0)
    : new Date(year, month - 1, day, hour, minute);
  const yGz = yearPillar(nextDayZi ? ey : year, dtForYear);
  const yGan = yGz[0];
  const yZhi = yGz[1];

  const jq = findJieqi(year, month, day, hour, minute);
  const dun: DunType = (input.dunOverride || jq.dun) as DunType;

  const mZhi = jq.zhi;
  const mOrd = monthOrdFromZhi(mZhi);
  const mGan = monthGan(yGan, mOrd);
  const mGz = mGan + mZhi;

  const hZhi = hz.zhi;
  const hGan = hourGan(dayP.gan, hZhi);
  const hGz = hGan + hZhi;

  // Program AG19=IF(I2=23, AH16, AH15)：23 時農曆取 Data (nextday)＝次日陰曆日
  const lunarY = nextDayZi ? ey : year;
  const lunarM = nextDayZi ? em : month;
  const lunarD = nextDayZi ? ed : day;
  const dateKey = `${lunarY}-${String(lunarM).padStart(2, '0')}-${String(lunarD).padStart(2, '0')}`;
  const lunar = LUNAR[dateKey];
  let lunarDay = lunar ?? 1;
  if (lunar == null) {
    notes.push('農曆日超出預表 1900–2100，局數用 ld=1 近似');
    lunarDay = 1;
  } else if (nextDayZi) {
    notes.push('23 時子時：局數農曆日用 Data (nextday)／次日（對照 Program AG19/AH16）');
  }

  const ac5 = zhiIndex(yZhi);
  const ac8 = mOrd;
  const ac9 = lunarDay;
  const ac12 = zhiIndex(hZhi);
  let ju =
    input.juOverride && input.juOverride >= 1 && input.juOverride <= 9
      ? input.juOverride
      : mod(ac5 + ac8 + ac9 + ac12, 9);
  if (ju === 0) ju = 9;

  const xun = xunInfo(hGz);
  const dipan = buildDipan(ju, dun);

  const zhifuPalace = palaceOfStem(dipan, xun.xunshouStem);
  const zhifuStar = starLabel(PALACE_STAR[zhifuPalace]);

  let zhishiDoor = PALACE_DOOR[zhifuPalace];
  if (zhishiDoor === '-') zhishiDoor = '死';

  const hourStemOnDi = hGan === '甲' ? xun.xunshouStem : hGan;
  const hourStemPalace = palaceOfStem(dipan, hourStemOnDi);

  const { tianGan, star, diShen, tianShen } = buildTianpanExcel(
    dipan,
    zhifuPalace,
    hourStemPalace,
    dun,
  );
  const { doors, zhishiPalace } = buildDoorsExcel(zhifuPalace, xun.offset, dun);

  const displayOrder = displayPalaceOrder(rot);

  // Program AR17：OR(時干=甲, 時干=值使門上天盤干 AT, 時干=AS15寄顯)
  // AS15 = IF(AR15=R11, S11, "")；R11/S11 依 AG6 顯示宮（寄坤→中地盤）
  const tianOnZhishi = tianGan[zhishiPalace] ?? '';
  const ag6 = displayOrder[2] ?? 2; // Program S8=AG6 → 九宮格 NE
  const r11 = dipan[ag6] ?? '';
  const s11 = r11 === (dipan[2] ?? '') ? (dipan[5] ?? '') : '';
  const as15 = tianOnZhishi === r11 ? s11 : '';
  const xunshouInZhong =
    hGan === '甲' || hGan === tianOnZhishi || (as15 !== '' && hGan === as15);

  // Program AS = IF(AR17="是", AR, AQ)；AR = 阳遁 CA／阴遁 CU；AQ = 阳遁 BW／阴遁 CQ
  // AQ 時干鍵：甲則用旬首干（對照 BK14/CE14 之 IF(Y11="甲", W12, Y11)）≡ hourStemOnDi
  let yinGanMap = buildYinGan(tianGan, dipan, zhishiPalace, hourStemOnDi);
  if (xunshouInZhong) {
    yinGanMap =
      dun === '阳遁'
        ? buildYinGanXunshouInZhongYang(xun.xunshouStem)
        : buildYinGanXunshouInZhongYin(xun.xunshouStem);
  }
  const markersFidelity: 'real' | 'approx' | 'stub' = 'real';

  const kongList = kongWangPalaces(xun.xunshouGz);
  const maPalace = maXingPalace(hZhi);
  const kongSet = new Set(kongList);

  // Program T8：離九宮門＝景→八門伏吟；＝休→八門反吟
  const doorAtLi = doors[9] ?? '';
  let menFuyin: '' | '八門伏吟' | '八門反吟' = '';
  if (doorAtLi === '景') menFuyin = '八門伏吟';
  else if (doorAtLi === '休') menFuyin = '八門反吟';

  // Program T9：離九天干＝地盤→天干伏吟；＝坎地盤→天干反吟
  const tgLi = tianGan[9] ?? '';
  const dgLi = dipan[9] ?? '';
  const dgKan = dipan[1] ?? '';
  let ganFuyin: '' | '天干伏吟' | '天干反吟' = '';
  if (tgLi && tgLi === dgLi) ganFuyin = '天干伏吟';
  else if (tgLi && tgLi === dgKan) ganFuyin = '天干反吟';

  const kunDi = dipan[2] ?? '';
  const zhongDi = dipan[5] ?? '';

  const palaces: PalaceContent[] = [];
  for (let p = 1; p <= 9; p++) {
    const shenRaw = tianShen[p] ?? '';
    const diShenRaw = diShen[p] ?? '';
    const door = doors[p] ?? '';
    const tg = tianGan[p] ?? '';
    const dg = dipan[p] ?? '';
    palaces.push({
      luoshu: p,
      name: PALACE_NAME[p],
      diGan: dg,
      tianGan: tg,
      star: starLabel(star[p] ?? ''),
      diPanXing: starLabel(PALACE_STAR[p] ?? ''),
      tianPanXing: starLabel(star[p] ?? ''),
      door,
      shen: shenRaw === '-' ? '' : shenRaw,
      diShen: diShenRaw === '-' ? '' : diShenRaw,
      yinGan: yinGanMap[p] ?? '',
      jiXianTian: jiXianExtra(tg, kunDi, zhongDi),
      jiXianDi: jiXianExtra(dg, kunDi, zhongDi),
      kongWang: kongSet.has(p),
      maXing: maPalace === p,
      po: isPo(p, door),
      ...evalXingMu(p, tg, dg, xun.xunshouStem, kunDi, zhongDi),
    });
  }

  if (!JIEQI_BY_YEAR[String(year)]) {
    notes.push('節氣超出預表 1900–2100，結果可能不準');
  }

  return {
    input,
    effective: {
      year: ey,
      month: em,
      day: ed,
      hour,
      minute,
      nextDayZi,
    },
    pillars: {
      year: yGz,
      month: mGz,
      day: dayP.gz,
      hour: hGz,
    },
    jieqi: { name: jq.name, dun: jq.dun, zhi: jq.zhi },
    dun,
    ju,
    juLabel: JU_LABELS[ju],
    xunshou: xun.xunshouGz,
    xunshouStem: xun.xunshouStem,
    zhifu: {
      star: zhifuStar,
      palace: zhifuPalace,
      palaceName: PALACE_NAME[zhifuPalace],
    },
    zhishi: {
      door: zhishiDoor,
      palace: zhishiPalace,
      palaceName: PALACE_NAME[zhishiPalace],
    },
    rotation: rot,
    palaces,
    displayOrder,
    kongWangPalaces: kongList,
    maXingPalace: maPalace,
    menFuyin,
    ganFuyin,
    xunshouInZhong,
    notes,
    fidelity: {
      pillars: 'real',
      jieqi: 'real',
      ju: 'real',
      plates: 'real',
      markers: markersFidelity,
    },
  };
}
